import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {Card, ListGroupItem, Text, Icon, Anchor} from '@albert-io/atomic';
import Markdown from 'components/Markdown/Markdown.react';

import './staging-details.scss';

/**
 * @example
 * <StagingDetails>
 *   <StagingDetails.Header
 *     title='Lorem ipsum dolor'
 *     subtitle='Hello world'
 *     badge={<Badge>Bleep Bloop</Badge>}
 *   />
 *   <StagingDetails.Body>
 *     <StagingDetails.Item
 *       warn
 *       label='Lorem ipsum dolor'
 *       details='Quisque placerat facilisis egestas cillum dolore'
 *       icon={['far', 'calendar-alt']}
 *     />
 *     <StagingDetails.Item
 *       label='Lorem ipsum dolor'
 *       details='Quisque placerat facilisis egestas cillum dolore'
 *       icon={['far', 'calendar-alt']}
 *     />
 *     <StagingDetails.Item
 *       label='Lorem ipsum dolor'
 *       details='Quisque placerat facilisis egestas cillum dolore'
 *       icon={['far', 'calendar-alt']}
 *     />
 *   </StagingDetails.Body>
 *   <StagingDetails.Footer>
 *     <Button>Testing</Button>
 *     <Button>Testing</Button>
 *   </StagingDetails.Footer>
 * </StagingDetails>
 */

const StagingDetails = ({
  children,
  sideBarImageWidth,
  className,
  sidebarImage = import('./library-blue.svg')
}) => {
  const [imagePath, setImagePath] = useState(null);
  useEffect(() => {
    (async () => {
      const path = await sidebarImage.then((module) => module.default);
      setImagePath(path);
    })();
  }, [sidebarImage]);
  return (
    <div className={classnames('staging-details', className)}>
      <div className='staging-details__img-wrapper'>
        <div
          className='staging-details__img'
          style={
            imagePath && {
              backgroundImage: `url(${imagePath})`,
              width: sideBarImageWidth
            }
          }
        />
      </div>
      <Card paddingLevel={0} className='staging-details__content'>
        {children}
      </Card>
    </div>
  );
};

StagingDetails.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  sidebarImage: PropTypes.string,
  sideBarImageWidth: PropTypes.string
};

const Header = ({title, subtitle, badge}) => {
  return (
    <div className='staging-details__header u-display_flex u-gap_2 u-pad_2 u-justify-content_space-between u-align-items_top'>
      <div>
        <Text bold className='u-mar-b_0 u-font-weight_900' size='l' as='h1'>
          {title}
        </Text>
        <Text emphasis='medium' size='m' as='p' className='u-mar-b_0'>
          {subtitle}
        </Text>
      </div>
      {badge}
    </div>
  );
};

Header.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  badge: PropTypes.node
};

const Body = ({children}) => (
  <ListGroupItem as='ul' paddingLevel={0} className='u-mar_0'>
    {children}
  </ListGroupItem>
);

Body.propTypes = {
  children: PropTypes.node
};

const Footer = ({children}) => {
  const childCount = React.Children.count(children);
  return (
    <ListGroupItem footer className='staging-details-footer'>
      <div
        className={classnames('u-display_flex', {
          'u-justify-content_space-between': childCount > 1,
          'u-justify-content_flex-end': childCount === 1
        })}
      >
        {children}
      </div>
    </ListGroupItem>
  );
};

Footer.propTypes = {
  children: PropTypes.node
};

const Item = ({label, details = null, additionalInfo, icon, warn, onLabelClick, className}) => {
  const LabelEl = onLabelClick ? Anchor : Text;
  let labelColor = 'black';
  if (onLabelClick) {
    labelColor = undefined;
  } else if (warn) {
    labelColor = 'red';
  }
  return (
    <li className={classnames('staging-detail-item', className)}>
      <div className='staging-detail-item__icon'>
        <Icon color={warn ? 'danger' : 'inherit'} icon={icon} fixedWidth className='u-mar-r_3' />
      </div>
      <div className='staging-detail-item__info'>
        <Text bold size='s' as='div'>
          <LabelEl
            size='m'
            as={onLabelClick ? 'button' : 'p'}
            color={labelColor}
            className='u-mar-b_0'
            onClick={onLabelClick}
          >
            {label}
          </LabelEl>
        </Text>
        {typeof details === 'string' ? (
          <Markdown
            as={(props) => <Text as='div' {...props} />}
            className={classnames('staging-detail-item__details', {
              'u-mar-b_1': !additionalInfo,
              'u-mar-b_0': !!additionalInfo
            })}
            size='m'
            text={details}
          />
        ) : (
          details
        )}
        {additionalInfo && (
          <Text
            italic
            className='staging-detail-item__additional'
            emphasis='lowest'
            as='p'
            size='s'
          >
            {additionalInfo}
          </Text>
        )}
      </div>
    </li>
  );
};

Item.propTypes = {
  label: PropTypes.string.isRequired,
  details: PropTypes.node,
  additionalInfo: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  warn: PropTypes.bool,
  onLabelClick: PropTypes.func,
  className: PropTypes.string
};

StagingDetails.Header = Header;
StagingDetails.Body = Body;
StagingDetails.Footer = Footer;
StagingDetails.Item = Item;

export default StagingDetails;
